import { Button, CloseButton, Divider, Grid, Group, Modal, Skeleton, Stack, Text } from '@mantine/core';
import { useIntl } from 'react-intl';

import { freightCarrierDetailsModalMessages } from './freight-carrier-details-modal-messages';
import styles from './FreightCarrierDetailsModal.module.css';
import { useFreightTerminalData } from '../../../hooks/use-freight-terminal-data';
import { getCarrierInitials } from '../get-carrier-initials';
import { getCarrierLogoSrc } from '../get-carrier-logo-src';

import type { FreightCarrierDetailsModalProps } from './freight-carrier-details-modal-types';
import type { FreightTerminalAddress } from '../../../types/freight-responses';

type TerminalColumnProps = {
  label: string;
  code?: string | null;
  address: FreightTerminalAddress | null;
  phone: string | null;
  isLoading: boolean;
};

const TerminalColumn = ({ label, code, address, phone, isLoading }: TerminalColumnProps) => (
  <Grid.Col span={1}>
    <Stack gap={4}>
      <Text className={styles.terminalLabel}>{label}</Text>
      {code && <Text className={styles.terminalCode}>{code}</Text>}
      {isLoading ? (
        <>
          <Skeleton height={14} radius='sm' width='90%' />
          <Skeleton height={14} radius='sm' width='60%' />
        </>
      ) : (
        <>
          {address && (address.line1 || address.line2) && (
            <Text className={styles.terminalAddress}>
              {address.line1}
              {address.line2 ? `, ${address.line2}` : ''}
            </Text>
          )}
          {address && (
            <Text className={styles.terminalAddress}>
              {address.city}, {address.state} {address.postalCode}
            </Text>
          )}
          {phone && <Text className={styles.terminalAddress}>{phone}</Text>}
        </>
      )}
    </Stack>
  </Grid.Col>
);

type FreightCarrierDetailsModalContentProps = Omit<FreightCarrierDetailsModalProps, 'opened' | 'zIndex'>;

const FreightCarrierDetailsModalContent = ({
  onClose,
  carrierName,
  carrierScac,
  quote,
  sellerFreightProviderId,
}: FreightCarrierDetailsModalContentProps) => {
  const intl = useIntl();
  const logoSrc = getCarrierLogoSrc(carrierScac);
  const { data, isLoading } = useFreightTerminalData({
    originCode: quote.originTerminalCode ?? null,
    destinationCode: quote.destinationTerminalCode ?? null,
    scac: carrierScac,
    sellerFreightProviderId: sellerFreightProviderId ?? null,
  });
  const hasLiabilityLimits = quote.maxLiabilityNew != null || quote.maxLiabilityUsed != null;

  const formatAmount = (value?: number | null, currency?: string | null) =>
    value != null ? intl.formatNumber(value, { style: 'currency', currency: currency ?? 'USD' }) : '—';

  return (
    <Stack gap='md' p='md'>
      <Group justify='space-between'>
        <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.title)}</Text>
        <CloseButton onClick={onClose} size='condensed' />
      </Group>

      <Group gap='sm'>
        <div className={styles.logoBox}>
          {logoSrc ? (
            <img alt={carrierName} className={styles.logoImage} src={logoSrc} />
          ) : (
            <span className={styles.logoInitials}>{getCarrierInitials(carrierName)}</span>
          )}
        </div>
        <Text fw={600}>{carrierName}</Text>
      </Group>

      <Divider />

      <Stack gap='xs'>
        <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.terminalInformation)}</Text>
        <Grid columns={2} gutter='md'>
          <TerminalColumn
            address={data?.origin?.address ?? null}
            code={quote.originTerminalCode}
            isLoading={isLoading}
            label={intl.formatMessage(freightCarrierDetailsModalMessages.pickupLabel)}
            phone={data?.origin?.phoneNumber ?? null}
          />
          <TerminalColumn
            address={data?.destination?.address ?? null}
            code={quote.destinationTerminalCode}
            isLoading={isLoading}
            label={intl.formatMessage(freightCarrierDetailsModalMessages.deliveryLabel)}
            phone={data?.destination?.phoneNumber ?? null}
          />
        </Grid>
      </Stack>

      <Divider />

      <Stack gap='xs'>
        <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.transitInformation)}</Text>
        <Text>{intl.formatMessage(freightCarrierDetailsModalMessages.estimatedTransit, { days: quote.transitDays })}</Text>
      </Stack>

      {hasLiabilityLimits && (
        <>
          <Divider />
          <Stack gap='xs'>
            <Text fw={600}>{intl.formatMessage(freightCarrierDetailsModalMessages.liabilityLimits)}</Text>
            <Text>
              {intl.formatMessage(freightCarrierDetailsModalMessages.liabilityNew, {
                amount: formatAmount(quote.maxLiabilityNew, quote.maxLiabilityNewCurrency),
              })}
            </Text>
            <Text>
              {intl.formatMessage(freightCarrierDetailsModalMessages.liabilityUsed, {
                amount: formatAmount(quote.maxLiabilityUsed, quote.maxLiabilityUsedCurrency),
              })}
            </Text>
          </Stack>
        </>
      )}

      <Group justify='flex-end'>
        <Button onClick={onClose} variant='tertiary'>
          {intl.formatMessage(freightCarrierDetailsModalMessages.close)}
        </Button>
      </Group>
    </Stack>
  );
};

export const FreightCarrierDetailsModal = ({ opened, onClose, zIndex, ...contentProps }: FreightCarrierDetailsModalProps) => (
  <Modal
    centered
    classNames={{ body: styles.modalBody, content: styles.modalContent }}
    onClose={onClose}
    opened={opened}
    size='md'
    withCloseButton={false}
    zIndex={zIndex}
  >
    <FreightCarrierDetailsModalContent onClose={onClose} {...contentProps} />
  </Modal>
);
